import React from 'react';

import Thumb from '../img/blogs/javascript-algorithm.jpg';

const JavaScriptAlgorithm = {
    id: 15, permalink: "javascript-algorithm", thumb: Thumb, postBy: "Wasek Bellah", postDate: "May 09, 2020", categories: "JavaScript, Algorithm", title: "JavaScript- Algorithm", shortDesc: "An algorithm is an unequivocal specification of how to solve a class of problems. It is a set of rules that define the order of activities.", fullDesc: <>
        <h2>Algorithm</h2>
        <p>An <strong>Algorithm</strong> is an unequivocal specification of how to solve a <strong>class</strong> of problems. It is a set of rules that define the order of activities.</p>

        <p>It's a computer procedure that tells our computer exactly what steps to take to solve a problem or reach a goal. The components are called inputs, the results are called outputs.</p>

        <p><em>Every <strong>Algorithm</strong> should satisfy the following features</em>:</p>
        <ul>
            <li><strong>Input</strong><br />
                <small>There should be <code>0</code> or more inputs supplied externally to the <strong>Algorithm</strong>.</small></li>

            <li><strong>Output</strong><br />
                <small>There should be at least <code>1</code> output obtained.</small></li>

            <li><strong>Definiteness</strong><br />
                <small>Every step of the <strong>Algorithm</strong> should be clear and well defined.</small></li>

            <li><strong>Finiteness</strong><br />
                <small>The <strong>Algorithm</strong> should have a finite number of steps.</small></li>

            <li><strong>Correctness</strong><br />
                <small>Every step of the <strong>Algorithm</strong> must generate the correct output.</small></li>
        </ul>


        <h2>Algorithms by Paradigm</h2>
        <p>An <strong>Algorithmic paradigm</strong> is a generic method that implies the design of a class of <strong>Algorithms</strong>. It is more abstract than the concept of an <strong>Algorithm</strong>, just as an <strong>Algorithm</strong> is more abstract than a computer program.</p>


        <h2>Space Complexity</h2>
        <p>This is the amount of <strong>memory space</strong> required by the <strong>algorithm</strong> when applied. <strong>Space complexity</strong> must be taken seriously for <strong>multi-user</strong> systems and in situations where <strong>limited memory</strong> is available.</p>

        <p><em>An <strong>Algorithm</strong> usually requires space for the following components</em>:</p>
        <h4>Instruction space:</h4>
        <p>This is the space required to save the executable version of the <strong>program</strong>. This space is fixed but varies according to the number of lines of code in the <strong>program</strong>.</p>

        <h4>Data Space:</h4>
        <p>The space required to store all <strong>constant and variable</strong> values ​​(temporary <strong>variables</strong> also).</p>

        <h4>Environment Space:</h4>
        <p>This is the space needed to store <strong>environment</strong> information needed to resume a suspended <strong>function</strong>.</p>


        <h2>Factorial</h2>
        <p>The <strong>factorial</strong> of a natural number is a number that is multiplied by <code>"number minus one"</code>, then by <code>"number minus two"</code>, and so on till <code>1</code>. The factorial of <code>n</code> is denoted as <code>n!</code></p>

        <pre className="code">
            n! = n * (n-1) * (n-2) * (n-3) * .... * 1
        </pre>

        <p><em><strong>Factorial</strong> values for different <code>n</code></em>:</p>
        <pre className="code">
            0! = 1 <br />
            1! = 1 <br />
            2! = 2 * 1 = 2 <br />
            3! = 3 * 2 * 1 = 6 <br />
            4! = 4 * 3 * 2 * 1 = 24 <br />
            5! = 5 * 4 * 3 * 2 * 1 = 120 <br />
        </pre>
        <p>The value of <code>0!</code> is <code>1</code>, according to the convention for an <strong>empty product</strong>.</p>


        <h2>Fibonacci Number</h2>
        <p>The <strong>Fibonacci</strong> Sequence is a series of numbers, starting from <code>0</code> and <code>1</code>. Each subsequent number is the sum of the previous two numbers. It's denoted by <span className="boldItalic">F<sub>n</sub></span>.</p>

        <pre className="code">
            0, 1, 1, 2, 3, 5, 8, 13, 21, 34, . . .
        </pre>

        <p><strong>Here Presentation:</strong> <code className="output">0 + 1(first  two numbers) = 1, 1 + 1 = 2, 1 + 2 = 3, 2 + 3 = 5, 3 + 5 = 8, 5 + 8 = 13, 8 + 13 = 21</code> and so on.</p>


        <h2>Linear Search</h2>
        <p>A <strong>linear search</strong> is a method for finding target value in a <strong>list</strong>. It <strong>sequentially</strong> checks each element of the list for the target <strong>value</strong> until a matched <strong>value</strong> is found. This is also known as <strong>sequential search</strong></p>

        <p><em><strong>A linear search function in JavaScript:</strong></em></p>
        <pre className="code">
            const linearSearch = (list, item) => &#123; <br />
                &nbsp; let matched = list.filter(i => i === item); <br />
                &nbsp; return matched.length !== 0 ? <br />
                &nbsp; console.log(item + " fount in " + list + " list and this is " + list.indexOf(item) + " number index in the list.") : <br />
                &nbsp; console.log(item + " not fount in " + list + " list."); <br />
            &#125; <br />
            const numList = [26, 15, 19, 34, 28, 17, 36]; <br />
            linearSearch(numList, 17);
        </pre>
        <p><strong>Expected output:</strong> <code className="output">17 fount in 26,15,19,34,28,17,36 list and this is 5 number index in the list.</code></p>


        <h2>Binary Search</h2>
        <p>A <strong>Binary search</strong> is a <strong>search algorithm</strong> that finds the position of the target value in a <strong>sorted array</strong>. <strong>Binary Search</strong> compares the target value to the middle element of the <strong>array</strong>. If they are unequal, half of the targets are removed and the search continues for the rest half until they are successful. If the search ends with the other half empty, the target is not in the <strong>array</strong>. This is also known as <strong>half-interval search, logarithmic search, or binary chop</strong>.</p>

        <pre className="code">
            const binarySearch = (list, item) => &#123; <br />
                &nbsp; let firstIndex = 0, <br />
                &nbsp; lastIndex = list.length - 1, <br />
                &nbsp; middleIndex = Math.floor((lastIndex + firstIndex) / 2); <br /><br />

                &nbsp; while (list[middleIndex] != item &amp;&amp; firstIndex &lt; lastIndex) &#123; <br />
                    &nbsp;   if (item &lt; list[<br />
                    &nbsp;   else if (item &gt; list[middleIndex]) firstIndex = middleIndex + 1; <br />
                    &nbsp;   middleIndex = Math.floor((lastIndex + firstIndex) / 2); <br />
                &nbsp; &#125; <br />
                &nbsp; return (list[middleIndex] != item) ? -1 : middleIndex; <br />
            &#125; <br />
            const items = [1, 2, 3, 4, 5, 7, 8, 9, 10]; <br />
            console.log(binarySearch(items, 1)); <br />
            console.log(binarySearch(items, 8));
        </pre>
        <p><strong>Expected output:</strong> <code className="output">0</code> and <code className="output">6</code></p>


        <h2>Bubble Sort</h2>
        <p><strong>Bubble Sort</strong> is a <strong>sorting algorithm</strong> that repeatedly takes steps to sort the list, compares each pair of adjacent items and sorts them if they are in the wrong order (<strong>ascending or descending</strong> order). Passing through the list is repeated until no match is required, indicating that the list has been sorted. This is also known as a <strong>sinking sort</strong>.</p>

        <pre className="code">
            const bubbleSort = list => &#123; <br />
                &nbsp; let swap; <br />
                &nbsp; let n = list.length - 1; <br />
                &nbsp; let x = list; <br />
                &nbsp; do &#123; <br />
                    &nbsp;   swap = false; <br />
                    &nbsp;   for (let i = 0; i &lt; n; <br />
                        &nbsp;     if (x[i] &gt; x[i + 1]) &#123; <br />
                            &nbsp;       let temp = x[i]; <br />
                            &nbsp;       x[i] = x[i + 1]; <br />
                            &nbsp;       x[i + 1] = temp; <br />
                            &nbsp;       swap = true; <br />
                        &nbsp;     &#125; <br />
                    &nbsp;   &#125; <br />
                    &nbsp;   n--; <br />
                &nbsp; &#125; while (swap); <br />
                &nbsp; return x; <br />
            &#125; <br />
            const numList = [26, 15, 19, 34, 28, 17, 36]; <br />
            console.log(bubbleSort(numList));
        </pre>
        <p><strong>Expected output:</strong> <code className="output">[15, 17, 19, 26, 28, 34, 36]</code></p>


        <h2>Selection Sort</h2>
        <p><strong>Selection Sort</strong> is an <strong>in-place comparison sort</strong>. It has <strong>O(n<sup>2</sup>)</strong> time complexity. It performs very slow on larger dataset. It's noted for its simplicity and its performance advantages over more complex <strong>algorithms</strong> in certain situations, especially where <strong>auxiliary memory</strong> is limited.</p>

        <pre className="code">
            const selectionSort = (list, compareFunction) => &#123; <br />
                &nbsp; const compare = (a, b) => a - b; <br />
                &nbsp; let min = 0; <br />
                &nbsp; let index = 0; <br />
                &nbsp; let temp = 0; <br />
                &nbsp; compareFunction = compareFunction || compare; <br /><br />

                &nbsp; for (let i = 0; i &lt; list.length; i++) &#123; <br />
                    &nbsp;   index = i; <br />
                    &nbsp;   min = list[i]; <br />
                    &nbsp;   for (let j = i + 1; j &lt; list.length; j++) &#123; <br />
                        &nbsp;     if (compareFunction(min, list[j]) &gt; 0) &#123; <br />
                            &nbsp;       min = list[j]; <br />
                            &nbsp;       index = j; <br />
                        &nbsp;     &#125; <br />
                    &nbsp;   &#125; <br />
                    &nbsp;   temp = list[i]; <br />
                    &nbsp;   list[i] = min; <br />
                    &nbsp;   list[index] = temp; <br />
                &nbsp; &#125; <br />
                &nbsp; return list; <br />
            &#125; <br />
            const numList = [26, 15, 19, 34, 28, 17, 36]; <br />
            console.log(selectionSort(numList, (a, b) => a - b)); <br />
            console.log(selectionSort(numList, (a, b) => b - a));
        </pre>
        <p><strong>Expected output:</strong> <code className="output">[15, 17, 19, 26, 28, 34, 36]</code> and <code className="output">[36, 34, 28, 26, 19, 17, 15]</code></p>

        <h2>Insertion Sort</h2>
        <p><strong>Insertion Sort</strong> is a simple <strong>sorting algorithm</strong> that creates a final sorted list at once and it works best for data that is already mostly sorted. It is much less efficient on large lists than more advanced <strong>algorithms</strong>.</p>

        <pre className="code">
            const insertionSort = list => &#123; <br />
                &nbsp; for (let i = 1; i &lt; list.length; i++) &#123; <br />
                    &nbsp;   let j = i - 1; <br />
                    &nbsp;   let temp = list[i]; <br />
                    &nbsp;   while (j >= 0 &amp;&amp; list[j] > temp) &#123; <br />
                        &nbsp;     list[j + 1] = list[j]; <br />
                        &nbsp;     j--; <br />
                    &nbsp;   &#125; <br />
                    &nbsp;   list[j + 1] = temp; <br />
                &nbsp; &#125; <br />
                &nbsp; return list; <br />
            &#125; <br />
            const numList = [26, 15, -13, 19, 12, 34, 28, -8, 17, 36]; <br />
            console.log(insertionSort(numList));
        </pre>
        <p><strong>Expected output:</strong> <code className="output">[-13, -8, 12, 15, 17, 19, 26, 28, 34, 36]</code></p>
    </>
};
export default JavaScriptAlgorithm;